import React, { useState, useMemo } from 'react';
import { Trip, University, Booking } from '../../../types';
import {
  ChevronRight,
  ChevronLeft,
  Calendar as CalendarIcon,
  Bus,
  Repeat,
  Eye,
  Filter,
  Clock,
  MapPin,
  X,
} from 'lucide-react';

interface AdminBookingCalendarProps {
  trips: Trip[];
  bookings: Booking[];
  universities: University[];
  onSelectTrip: (trip: Trip) => void;
  onNewBookingClick?: (defaultDate?: string) => void;
}

const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

const WEEKDAY_NAMES = [
  'السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة',
];

export const AdminBookingCalendar: React.FC<AdminBookingCalendarProps> = ({
  trips,
  bookings,
  universities,
  onSelectTrip,
  onNewBookingClick,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedUniFilter, setSelectedUniFilter] = useState<string>('ALL');
  const [dayDetailsDate, setDayDetailsDate] = useState<string | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); // 0-indexed

  const monthTitle = `${ARABIC_MONTHS[month]} ${year}`;

  const prevMonth = () => {
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const todayIso = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  // Filter trips by university if selected
  const filteredTrips = useMemo(() => {
    return trips.filter((t) => {
      const matchUni = selectedUniFilter === 'ALL' || t.universityId === selectedUniFilter;
      return matchUni && t.status !== 'CANCELLED';
    });
  }, [trips, selectedUniFilter]);

  // Group trips and bookings by ISO date string (YYYY-MM-DD)
  const dayStatsMap = useMemo(() => {
    const map: Record<
      string,
      {
        outboundTrips: Trip[];
        returnTrips: Trip[];
        totalTrips: number;
        totalCapacity: number;
        totalBookedSeats: number;
        trips: Array<{
          trip: Trip;
          capacity: number;
          bookedSeats: number;
          availableSeats: number;
        }>;
      }
    > = {};

    for (const t of filteredTrips) {
      if (!t.date) continue;
      if (!map[t.date]) {
        map[t.date] = {
          outboundTrips: [],
          returnTrips: [],
          totalTrips: 0,
          totalCapacity: 0,
          totalBookedSeats: 0,
          trips: [],
        };
      }

      const capacity = t.totalSeats || t.capacity || 50;
      // Count active bookings for this trip
      const tripActiveBookings = bookings.filter(
        (b) =>
          b.tripId === t.id &&
          (b.status || b.bookingStatus) !== 'CANCELLED' &&
          (b.status || b.bookingStatus) !== 'POSTPONED'
      );
      const bookedCount = tripActiveBookings.length;
      const availCount = Math.max(0, capacity - bookedCount);

      if (t.direction === 'RETURN') {
        map[t.date].returnTrips.push(t);
      } else {
        map[t.date].outboundTrips.push(t);
      }

      map[t.date].totalTrips += 1;
      map[t.date].totalCapacity += capacity;
      map[t.date].totalBookedSeats += bookedCount;

      map[t.date].trips.push({
        trip: t,
        capacity,
        bookedSeats: bookedCount,
        availableSeats: availCount,
      });
    }

    return map;
  }, [filteredTrips, bookings]);

  // Compute days matrix for the month (Saturday to Friday)
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();

    // In JS: 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    // We want 0=Sat, 1=Sun, ..., 6=Fri
    const firstDayJs = firstDay.getDay();
    const leadingEmptyDays = (firstDayJs + 1) % 7; // If Sat (6): (6+1)%7 = 0. If Sun (0): 1.

    const cells: Array<{
      dayNumber: number;
      dateIso: string;
      isCurrentMonth: boolean;
      isToday: boolean;
    }> = [];

    // Leading empty cells
    for (let i = 0; i < leadingEmptyDays; i++) {
      cells.push({
        dayNumber: 0,
        dateIso: '',
        isCurrentMonth: false,
        isToday: false,
      });
    }

    // Days of current month
    for (let d = 1; d <= totalDays; d++) {
      const dateIso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({
        dayNumber: d,
        dateIso,
        isCurrentMonth: true,
        isToday: dateIso === todayIso,
      });
    }

    return cells;
  }, [year, month, todayIso]);

  // Selected Day Trips
  const dayTripsData = useMemo(() => {
    if (!dayDetailsDate) return null;
    return dayStatsMap[dayDetailsDate] || null;
  }, [dayDetailsDate, dayStatsMap]);

  return (
    <div className="booking-calendar-wrapper">
      {/* Calendar Header Controls */}
      <div className="cal-controls-header">
        <div className="cal-month-nav-group">
          <button
            type="button"
            className="cal-nav-btn"
            onClick={prevMonth}
            title="الشهر السابق"
          >
            <ChevronRight size={20} />
          </button>

          <span className="cal-month-title">{monthTitle}</span>

          <button
            type="button"
            className="cal-nav-btn"
            onClick={nextMonth}
            title="الشهر القادم"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            type="button"
            className="btn-admin-outline"
            style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem' }}
            onClick={goToToday}
          >
            اليوم الحالي
          </button>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* University Filter */}
          <div className="admin-filter-group">
            <Filter size={15} className="text-slate-400" />
            <select
              className="admin-filter-select"
              value={selectedUniFilter}
              onChange={(e) => setSelectedUniFilter(e.target.value)}
              style={{ minWidth: '180px' }}
            >
              <option value="ALL">جميع خطوط الجامعات</option>
              {universities.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          {/* Quick Legend */}
          <div className="cal-legend-strip desktop-only-flex">
            <div className="cal-legend-item">
              <span className="cal-legend-dot outbound" />
              <span>رحلات الذهاب</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-dot return" />
              <span>رحلات العودة</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-dot available" />
              <span>شاغر متاح</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-dot full" />
              <span>مكتمل الحجز</span>
            </div>
          </div>
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="booking-month-grid">
        {WEEKDAY_NAMES.map((name) => (
          <div key={name} className="cal-weekday-header-cell">
            {name}
          </div>
        ))}

        {/* Days Cells */}
        {calendarDays.map((cell, idx) => {
          if (!cell.isCurrentMonth) {
            return <div key={`empty-${idx}`} className="booking-day-cell is-empty" />;
          }

          const stats = dayStatsMap[cell.dateIso];
          const hasTrips = !!stats && stats.totalTrips > 0;
          const occupancyPercent =
            hasTrips && stats.totalCapacity > 0
              ? Math.round((stats.totalBookedSeats / stats.totalCapacity) * 100)
              : 0;

          return (
            <div
              key={cell.dateIso}
              className={`booking-day-cell ${cell.isToday ? 'is-today' : ''} ${
                dayDetailsDate === cell.dateIso ? 'is-selected' : ''
              }`}
              onClick={() => {
                if (hasTrips) {
                  setDayDetailsDate(cell.dateIso);
                } else if (onNewBookingClick) {
                  onNewBookingClick(cell.dateIso);
                }
              }}
              title={
                hasTrips
                  ? `اضغط لعرض رحلات يوم ${cell.dateIso} (${stats.totalTrips} رحلات)`
                  : `لا توجد رحلات مجدولة ليوم ${cell.dateIso}`
              }
            >
              {/* Day Cell Top Info */}
              <div className="day-cell-top">
                <span className="day-number-badge">{cell.dayNumber}</span>
                {cell.isToday && <span className="day-today-tag">اليوم</span>}
              </div>

              {/* Counts of Trips */}
              {hasTrips ? (
                <>
                  <div className="day-counts-strip">
                    {stats.outboundTrips.length > 0 && (
                      <div className="day-count-badge outbound">
                        <span className="flex items-center gap-1">
                          <Bus size={12} />
                          <span>ذهاب</span>
                        </span>
                        <span>{stats.outboundTrips.length} رحلة</span>
                      </div>
                    )}

                    {stats.returnTrips.length > 0 && (
                      <div className="day-count-badge return">
                        <span className="flex items-center gap-1">
                          <Repeat size={12} />
                          <span>عودة</span>
                        </span>
                        <span>{stats.returnTrips.length} رحلة</span>
                      </div>
                    )}
                  </div>

                  {/* Seat Occupancy Meter */}
                  <div className="day-seats-progress-box">
                    <div className="day-seats-meta">
                      <span>المحجوز:</span>
                      <span className="font-mono">
                        {stats.totalBookedSeats}/{stats.totalCapacity} مقعد
                      </span>
                    </div>
                    <div className="day-progress-track">
                      <div
                        className={`day-progress-bar ${
                          occupancyPercent >= 90
                            ? 'red'
                            : occupancyPercent >= 60
                            ? 'amber'
                            : 'green'
                        }`}
                        style={{ width: `${Math.min(100, occupancyPercent)}%` }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-slate-300 font-bold">
                  لا توجد رحلات
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Day Details Modal (Opens when user clicks on any day) */}
      {dayDetailsDate && dayTripsData && (
        <div className="admin-modal-overlay" onClick={() => setDayDetailsDate(null)}>
          <div
            className="admin-modal-card"
            style={{ maxWidth: '820px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-header">
              <div className="flex items-center gap-2">
                <CalendarIcon size={22} className="text-[#E5232A]" />
                <h3 className="text-lg font-bold text-slate-900">
                  جدول رحلات يوم: {dayDetailsDate}
                </h3>
              </div>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setDayDetailsDate(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-modal-body">
              {/* Day Meta Strip */}
              <div className="p-3 bg-slate-50 border rounded-xl flex items-center justify-between flex-wrap gap-2 text-sm">
                <div>
                  <span className="text-slate-500 block text-xs">إجمالي الرحلات:</span>
                  <strong className="text-slate-800">{dayTripsData.totalTrips} رحلات مجدولة</strong>
                </div>
                <div>
                  <span className="text-slate-500 block text-xs">إجمالي المقاعد المحجوزة:</span>
                  <strong className="text-emerald-700 font-mono">
                    {dayTripsData.totalBookedSeats} من {dayTripsData.totalCapacity} مقعد
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500 block text-xs">المقاعد الشاغرة المتبقية:</span>
                  <strong className="text-blue-700 font-mono">
                    {Math.max(0, dayTripsData.totalCapacity - dayTripsData.totalBookedSeats)} مقعد متاح
                  </strong>
                </div>
              </div>

              {/* Trip Cards for This Day */}
              <div className="mt-4">
                <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Bus size={16} className="text-[#E5232A]" />
                  <span>اختر الرحلة لعرض منافيست الركاب وتعديل المقاعد:</span>
                </h4>

                <div className="day-trips-list-grid">
                  {dayTripsData.trips.map(({ trip, capacity, bookedSeats, availableSeats }) => {
                    const isFull = availableSeats <= 0;
                    return (
                      <div
                        key={trip.id}
                        className="day-trip-card"
                        onClick={() => {
                          setDayDetailsDate(null);
                          onSelectTrip(trip);
                        }}
                      >
                        <div className="day-trip-header">
                          <span className="day-trip-time flex items-center gap-1.5">
                            <Clock size={16} className="text-slate-400" />
                            <span>{trip.departureTime}</span>
                          </span>
                          <span
                            className={`dir-badge ${
                              trip.direction === 'RETURN' ? 'return' : 'outbound'
                            }`}
                          >
                            {trip.direction === 'RETURN' ? 'عودة للإسكندرية' : 'ذهاب للجامعة'}
                          </span>
                        </div>

                        <div>
                          <strong className="text-sm font-bold text-slate-900 block">
                            {trip.universityName}
                          </strong>
                          <span className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin size={12} />
                            <span>{trip.destination}</span>
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-100">
                          <span className="font-mono font-bold text-slate-700">
                            سعر التذكرة: {trip.price} ج.م
                          </span>
                          <span
                            className={`day-trip-occupancy-pill ${
                              isFull ? 'full' : 'has-seats'
                            }`}
                          >
                            {bookedSeats}/{capacity} محجوز
                          </span>
                        </div>

                        <button
                          type="button"
                          className="btn-admin-primary w-full flex items-center justify-center gap-1.5 text-xs py-2 mt-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDayDetailsDate(null);
                            onSelectTrip(trip);
                          }}
                        >
                          <Eye size={14} />
                          <span>عرض الركاب وإدارة المقاعد</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button
                type="button"
                className="btn-admin-outline"
                onClick={() => setDayDetailsDate(null)}
              >
                إغلاق النافذة
              </button>
              {onNewBookingClick && (
                <button
                  type="button"
                  className="btn-admin-primary flex items-center gap-1.5"
                  onClick={() => {
                    const date = dayDetailsDate;
                    setDayDetailsDate(null);
                    onNewBookingClick(date);
                  }}
                >
                  <Bus size={15} />
                  <span>حجز مقعد لطالب في هذا اليوم</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
